import React from 'react';

import BenchlingConfig from 'BenchlingConfig';
import FileState from 'filestate/FileState';
import {TemplateFileState} from 'FileStates';
import EntryTemplateHeader, {
  type EntryTemplateHeaderProps,
} from 'notebook/entry-templates/EntryTemplateHeader';

interface SubworkspaceHeaderMetadata<TProps> {
  /** The FileState subclass for which to render a fancy header */
  fileState: {new (attributes?: object, options?: object): FileState};
  /**
   * Wrapper component for the fancy header. Should live in a directory corresponding to and codeowned by the
   * team who owns that sort of folder item.
   */
  HeaderComponent: React.FC<TProps>;
  /**
   * Function to create HeaderComponent props from the fileState. Do NOT pass the Backbone FileState into your
   * React component, that way lies madness.
   */
  propsFromFileState: (fileState: FileState) => TProps;
  /**
   * If true, the header will not actually be rendered. This should probably be !BenchlingConfig.[something].
   */
  suppress?: boolean;
}

export const FILESTATE_TO_HEADER: Array<SubworkspaceHeaderMetadata<object>> = [
  {
    fileState: TemplateFileState,
    HeaderComponent: EntryTemplateHeader,
    propsFromFileState: (fileState: TemplateFileState): EntryTemplateHeaderProps => {
      const templateFile = fileState.model();
      return {
        allowEdits: templateFile.allowEdits(),
        collectionName: templateFile.templateCollection()?.name,
        onTitleChange: async (newTitle: string) => {
          await templateFile.triggerUpdateNameAction(newTitle);
        },
        name: templateFile.name(),
        reviewStatus: templateFile.review()?.status,
      };
    },
    suppress: !BenchlingConfig.ELN_ENABLE_UNIFIED_HEADER,
  },
];

export function getSubworkspaceHeaderMetadata(
  fileState: FileState
): Omit<SubworkspaceHeaderMetadata<object>, 'fileState'> | null {
  for (const obj of FILESTATE_TO_HEADER) {
    if (fileState instanceof obj.fileState) {
      return {
        HeaderComponent: obj.HeaderComponent,
        propsFromFileState: obj.propsFromFileState,
        suppress: obj.suppress,
      };
    }
  }
  return null;
}
